import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AMB, DARK_INVERT, SPRITES, seeds } from '../../lib/ambiances';
import type { AmbianceKey, ThemeMode } from '../../lib/ambiances';
import { useAmbiance } from './AmbianceContext';
import { VideoEnBoucle } from './VideoEnBoucle';

/**
 * La bande animée d'une ambiance (passation §7). Elle vit dans l'en-tête, la page de
 * connexion et la vitrine — jamais derrière une grille ou un tableau, jamais dans le kiosque.
 * Les @keyframes ambFall / ambCloud sont déclarés une fois dans index.css.
 * `prefers-reduced-motion` fige tout : seul l'accent (et la photo fixe) subsiste.
 */
interface AmbianceBandProps {
    /** Forcer une ambiance (aperçu dans le réglage super admin) ; sinon celle de la plateforme. */
    ambiance?: AmbianceKey;
    /** Forcer un thème ; sinon celui de la page. */
    mode?: ThemeMode;
    className?: string;
    style?: React.CSSProperties;
    /** Voile posé sur la vidéo/photo ; `null` pour aucun, sinon celui de l'ambiance. */
    veil?: string | null;
    children?: React.ReactNode;
}

const useReducedMotion = () => {
    const [calm, setCalm] = useState(() => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches);
    useEffect(() => {
        const mq = window.matchMedia?.('(prefers-reduced-motion: reduce)');
        if (!mq) return;
        const onChange = () => setCalm(mq.matches);
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);
    return calm;
};

export const AmbianceBand: React.FC<AmbianceBandProps> = ({ ambiance, mode, className = '', style, veil, children }) => {
    const ctx = useAmbiance();
    const key = ambiance || ctx.active;
    const m = mode || ctx.mode;
    const t = AMB[key];
    const calm = useReducedMotion();
    const ref = useRef<HTMLDivElement>(null);
    const [fall, setFall] = useState(200);

    // La distance de chute suit la hauteur réelle de la bande, sinon les visuels sortent du cadre.
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const update = () => setFall(Math.max(120, el.offsetHeight + 60));
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const sceneKey = (m === 'D' ? t.sceneD : t.sceneL) || t.scene;
    const scene = sceneKey ? SPRITES[sceneKey] : null;
    const wantVideo = !!t.video && !calm && (!t.videoOnly || t.videoOnly === m);
    const wantPhoto = !wantVideo && !!t.photo && (!t.photoOnly || t.photoOnly === m);
    const pool = useMemo(() => seeds(scene ? scene.n : 0), [scene]);

    return (
        <div ref={ref} className={`relative overflow-hidden ${className}`} style={{ background: m === 'D' ? t.bandD : t.bandL, ...style }}>
            <div aria-hidden="true" className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
                {/* Fondu enchaîné plutôt que `loop` : voir VideoEnBoucle, la coupure au rebouclage se voyait. */}
                {wantVideo && <VideoEnBoucle src={t.video!} poster={t.photo} />}
                {wantPhoto && (
                    <div className="absolute inset-0" style={{ backgroundImage: `url(${t.photo})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                )}
                {(wantVideo || wantPhoto) && veil !== null && (veil || t.veil) && <div className="absolute inset-0" style={{ background: veil || t.veil }} />}
                {/* Halo de soleil (jour) : sous les nuages, jamais avec une vidéo ou une photo. */}
                {!wantVideo && !wantPhoto && (m === 'D' ? t.sunD : t.sunL) && <div className="absolute inset-0" style={{ background: m === 'D' ? t.sunD : t.sunL }} />}

                {scene && !calm && pool.map((p, i) => {
                    const src = scene.list[i % scene.list.length];
                    if (scene.drift) {
                        // Trois plans de profondeur : le fond est petit, pâle et lent, le premier plan
                        // large, net et rapide. Sans cela les nuages forment une frise plate.
                        const depth = i % 3;
                        const far = depth === 0, near = depth === 2;
                        const w = far ? 96 + (i % 3) * 26 : near ? 250 + (i % 3) * 80 : 150 + (i % 3) * 44;
                        return (
                            <img key={`c${i}`} src={src} alt="" className="absolute" style={{
                                left: '0%', top: `${far ? -6 + (i % 3) * 9 : near ? 34 + (i % 3) * 22 : 6 + (i % 3) * 16}%`, width: `${w}px`, height: 'auto',
                                opacity: m === 'D' ? (far ? 0.14 : 0.3) : (far ? 0.45 : near ? 0.95 : 0.72),
                                filter: far ? 'blur(2.5px)' : near ? 'none' : 'blur(.6px)',
                                animation: `ambCloud ${(far ? 96 : near ? 44 : 66) + (i % 4) * 7}s linear infinite`, animationDelay: `-${i * 9.5}s`,
                            }} />
                        );
                    }
                    const flip = m === 'D' && DARK_INVERT[src];
                    const styleVars = { '--dx': `${p.dx}px`, '--rot': `${p.rot}deg`, '--o': '1', '--fall': `${fall}px` } as React.CSSProperties;
                    return (
                        <img key={`s${i}`} src={src} alt="" className="absolute" style={{
                            ...styleVars,
                            left: `${p.left}%`, top: '-20%', width: `${(scene.min || 16) + (i % 5) * (scene.step || 6)}px`, height: 'auto',
                            opacity: m === 'D' ? 0.92 : 1, filter: flip ? 'invert(1)' : 'none',
                            animation: `ambFall ${p.dur + (scene.dur || 10)}s ease-in-out infinite`, animationDelay: `-${p.delay + (i % 5) * 1.8}s`,
                        }} />
                    );
                })}
            </div>
            <div className="relative flex-1 flex flex-col" style={{ zIndex: 1 }}>{children}</div>
        </div>
    );
};
